import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  konkurrent,
  student,
} from '../../../components/common/common-functions/common-functions';
import { getCommonUrl } from '../../../components/common/common-functions/commonApiUrl/get-common-url';
import { handleDownloadResponse } from '../../../components/common/common-functions/handle-response';
import { ROLES } from '../../../constants/enums/roles';
import getConfig from '../../../helpers/config';
import { IdEntity, UpdateProps } from '../../../interfaces/common-interfaces';
import { RezultateFazaProps } from '../../../interfaces/fazat-interface';
import {
  CreateVerifikimeProps,
  KonkurrentiAktualVerifikime,
  KonkurrentiAplicationData,
  KonkurrentiData,
  KonkurrentiDocuments,
  PranoAplikiminProps,
  UpdateSpecifikaProps,
  UpdateVerifikimeProps,
  VerifikimPermbledhesReturn,
} from '../../../interfaces/konkurrenti-interface';
import {
  PaginationProps,
  PaginationReturn,
} from '../../../interfaces/pagination';
import { getPrepareHeaders } from '../get-prepare-headers';
const config = getConfig();

const { test } = config;

export interface KonkurrentiParams extends PaginationProps {
  emriMbiemri?: string;
  statusKonkurrent: string;
  vitiKonkurrimitId: number;
  sort?: 'asc' | 'desc';
}

export interface DocumentsProps {
  id: number;
  llojiDokumentKandidati: string;
  dokument: {
    id: number;
    emri: string;
    data: string;
  };
}

export interface DocumentListResponse {
  dokumentKandidatiDtoPage: PaginationReturn<DocumentsProps>;
  numriDokumenteveTeDorezuara: number;
}

export const konkurrentiApi = createApi({
  reducerPath: 'konkurrenti',
  baseQuery: fetchBaseQuery({
    baseUrl: test.baseUrl + `/`,
    prepareHeaders: getPrepareHeaders,
  }),
  tagTypes: [
    'Konkurrenti',
    'GetAllKonkurrenti',
    'konkurimeAktive',
    'konkurimeHistorike',
    'konkurrentiAktual',
    'Dokumente',
    'DokumenteById',
  ],
  endpoints: builder => ({
    getKonkurimeAktive: builder.query<
      PaginationReturn<KonkurrentiData>,
      KonkurrentiParams
    >({
      query: args => ({
        url: getCommonUrl(args, '/konkurrenti/lista-aktiv'),
      }),
      providesTags: ['konkurimeAktive', 'GetAllKonkurrenti'],
    }),
    getKonkurimeHistorike: builder.query<
      PaginationReturn<KonkurrentiData>,
      KonkurrentiParams
    >({
      query: args => ({
        url: getCommonUrl(args, '/konkurrenti/historik'),
      }),
      providesTags: ['konkurimeHistorike', 'GetAllKonkurrenti'],
    }),
    getActiveKonkurrenti: builder.query<KonkurrentiData, void>({
      query: () => ({
        url: `/konkurrenti/aktual`,
      }),
      providesTags: ['GetAllKonkurrenti', 'konkurimeAktive'],
    }),
    getKonkurrentiVerifikime: builder.query<KonkurrentiAktualVerifikime, void>({
      query: () => ({
        url: '/konkurrenti/aktual/verifikime',
      }),
      providesTags: ['GetAllKonkurrenti', 'konkurimeAktive'],
    }),
    getSingleKonkurrenti: builder.query<KonkurrentiData, number>({
      query: id => ({
        url: `/konkurrenti/${id}`,
      }),
      providesTags: ['Konkurrenti'],
    }),
    getKonkurrentiAktual: builder.query<RezultateFazaProps, void>({
      query: () => ({
        url: `/rezultate-faza/konkurrenti-aktual`,
      }),
      providesTags: ['konkurrentiAktual'],
    }),
    saveKonkurrentiAplication: builder.mutation<
      IdEntity,
      KonkurrentiAplicationData
    >({
      query: body => ({
        url: `/konkurrenti`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Konkurrenti', 'GetAllKonkurrenti', 'konkurimeAktive'],
    }),
    dorezoAplikimin: builder.mutation<void, number>({
      query: body => ({
        url: '/konkurrenti/dorezo-aplikimin',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['GetAllKonkurrenti', 'Konkurrenti'],
    }),
    updateAplication: builder.mutation<void, KonkurrentiAplicationData>({
      query: body => ({
        url: '/konkurrenti/perditeso-specifika',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['GetAllKonkurrenti', 'Konkurrenti', 'konkurimeAktive'],
    }),
    pranoAplikim: builder.mutation<number, PranoAplikiminProps>({
      query: data => {
        const { id, komenti, statusKonkurrent } = data;
        return {
          url: `konkurrenti/prano-aplikim/${id}`,
          method: 'PUT',
          body: { komenti, statusKonkurrent },
        };
      },
      invalidatesTags: ['GetAllKonkurrenti', 'Konkurrenti', 'konkurimeAktive'],
    }),
    perditesoSpecifikaPerKonkurrent: builder.mutation<
      number,
      UpdateSpecifikaProps
    >({
      query: args => ({
        url: `/konkurrenti/perditeso-specifika-per-konkurrent/${args.id}`,
        method: 'PUT',
        body: args.data,
      }),
      invalidatesTags: ['GetAllKonkurrenti', 'Konkurrenti', 'konkurimeAktive'],
    }),
    createVerifikime: builder.mutation<
      number,
      UpdateProps<CreateVerifikimeProps>
    >({
      query: args => ({
        url: `/verifikimi/${args.id}`,
        method: 'POST',
        body: args.data,
      }),
      invalidatesTags: ['GetAllKonkurrenti', 'Konkurrenti', 'konkurimeAktive'],
    }),
    updateVerifikimeStatus: builder.mutation<number, UpdateVerifikimeProps>({
      query: args => ({
        url: `/verifikimi/perditeso-status/${args.id}?status=${args.status}&koment=${args.koment}`,
        method: 'PUT',
        body: args,
      }),
      invalidatesTags: ['GetAllKonkurrenti', 'Konkurrenti', 'konkurimeAktive'],
    }),
    mbyllAplikime: builder.mutation({
      query: () => ({
        url: '/faza/mbyll-verifikime',
        method: 'POST',
        responseHandler: response => response.text(),
      }),
      invalidatesTags: ['GetAllKonkurrenti', 'Konkurrenti', 'konkurimeAktive'],
    }),
    shtoVerifikimPermbledhes: builder.mutation<
      number,
      VerifikimPermbledhesReturn
    >({
      query: args => ({
        url: `/konkurrenti/shto-verifikim-permbledhes/${args.id}`,
        method: 'PUT',
        body: args.data,
      }),
      invalidatesTags: ['GetAllKonkurrenti', 'Konkurrenti'],
    }),
    getAllDocuments: builder.query<DocumentListResponse, KonkurrentiDocuments>({
      query: args => ({
        url: [konkurrent, student].includes(args?.role as ROLES)
          ? getCommonUrl(args, '/konkurrenti/dokument')
          : `/konkurrenti/dokumente-konkurrenti/${args.konkurrentiId}?size=50&page=0`,
      }),
      providesTags: ['Dokumente'],
    }),
    getDocumentById: builder.query<
      Blob,
      { dokumentKandidatiPerKonkurimId: number }
    >({
      query: args => ({
        url: `/konkurrenti/dokument/${args.dokumentKandidatiPerKonkurimId}`,

        responseHandler: handleDownloadResponse,
      }),
      providesTags: ['DokumenteById', 'Dokumente'],
    }),
    getDocumentAdminById: builder.query<
      Blob,
      { dokumentKandidatiPerKonkurimId: number }
    >({
      query: args => ({
        url: `/konkurrenti/dokumente-konkurrenti/dokument/${args.dokumentKandidatiPerKonkurimId}`,

        responseHandler: handleDownloadResponse,
      }),
      providesTags: ['DokumenteById', 'Dokumente'],
    }),
    getExportDocument: builder.query<Blob, void>({
      query: () => ({
        url: '/konkurrenti/eksport-kerkese-per-aplikim',
        responseHandler: handleDownloadResponse,
      }),
    }),
    uploadNewDocument: builder.mutation<void, unknown>({
      query: body => ({
        url: '/konkurrenti/dokument',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Dokumente', 'DokumenteById'],
    }),
    deleteDocumentById: builder.mutation<number, unknown>({
      query: id => ({
        url: `/konkurrenti/dokument/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Dokumente'],
    }),
    ktheNeVerifikim: builder.mutation<void, number>({
      query: id => ({
        url: `/konkurrenti/kthe-ne-verifikim/${id}`,
        method: 'PUT',
      }),
      invalidatesTags: ['GetAllKonkurrenti', 'Konkurrenti', 'konkurimeAktive'],
    }),
    exportDailyApplicationsReport: builder.query<Blob, void>({
      query: () => ({
        url: `/konkurrenti/export-daily-application`,
        responseHandler: handleDownloadResponse,
      }),
    }),
    exportVerificationReportFinal: builder.query<Blob, void>({
      query: () => ({
        url: `/konkurrenti/export-kandidatet-verifikim-final-per-profil`,
        responseHandler: handleDownloadResponse,
      }),
    }),
    exportVerificationReportShm: builder.query<Blob, void>({
      query: () => ({
        url: `/konkurrenti/export-kandidatet-verifikim-shm-per-profil`,
        responseHandler: handleDownloadResponse,
      }),
    }),
  }),
});
export const {
  useGetActiveKonkurrentiQuery,
  useGetKonkurrentiVerifikimeQuery,
  useGetSingleKonkurrentiQuery,
  useGetKonkurimeAktiveQuery,
  useGetKonkurimeHistorikeQuery,
  useGetKonkurrentiAktualQuery,
  useSaveKonkurrentiAplicationMutation,
  useDorezoAplikiminMutation,
  useUpdateAplicationMutation,
  usePranoAplikimMutation,
  usePerditesoSpecifikaPerKonkurrentMutation,
  useCreateVerifikimeMutation,
  useUpdateVerifikimeStatusMutation,
  useMbyllAplikimeMutation,
  useShtoVerifikimPermbledhesMutation,
  useGetAllDocumentsQuery,
  useLazyGetDocumentByIdQuery,
  useLazyGetDocumentAdminByIdQuery,
  useUploadNewDocumentMutation,
  useDeleteDocumentByIdMutation,
  useLazyGetExportDocumentQuery,
  useKtheNeVerifikimMutation,
  useLazyExportDailyApplicationsReportQuery,
  useLazyExportVerificationReportFinalQuery,
  useLazyExportVerificationReportShmQuery,
} = konkurrentiApi;
